"use client";

import { useEffect, useState } from "react";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { useHeaderOffset } from "@/lib/use-header-offset";
import { READING_PROGRESS_BAR_HEIGHT_PX } from "./reading-progress-bar";
import type { StockAnalysisHeading } from "@/lib/stock-analyses/headings";

/** Referenzlinie fuer die Scroll-Spy-Neuberechnung, in Pixeln von der Viewport-Oberkante -
 * knapp unterhalb von sticky Header + Fortschrittsbalken. */
const ACTIVE_HEADING_REFERENCE_LINE_PX = 120;

/**
 * Scroll-synchronisiertes Inhaltsverzeichnis (Erik-Wunsch 2026-08-04, Vorbild
 * Aktienfinder-Artikel-Layout - nur Inspiration, nicht nachgebaut).
 *
 * "Aktiv" ist die LETZTE Ueberschrift, deren Oberkante die Referenzlinie bereits
 * passiert hat - bei jedem IntersectionObserver-Trigger wird ueber ALLE Ueberschriften
 * per getBoundingClientRect() neu berechnet, statt nur auf ein schmales
 * rootMargin-Intersecting-Band der jeweils ausloesenden Entry zu vertrauen. Eine erste
 * Version mit einem schmalen "-45% bis -50%"-Band (nur ~5% Viewport-Hoehe) blieb nach
 * einem grossen Scroll-Sprung (z.B. Bildlauf-Leiste ziehen, Seite-runter-Taste) auf dem
 * ersten Abschnitt haengen, weil die Ueberschrift das schmale Band zwischen zwei
 * Messungen komplett uebersprang, ohne je als "intersecting" erfasst zu werden - live
 * mit einem eigenen Playwright-Testlauf gefunden, nicht nur vermutet.
 */
function useActiveHeadingId(headingIds: string[]): string | null {
  const [activeId, setActiveId] = useState<string | null>(headingIds[0] ?? null);

  useEffect(() => {
    const elements = headingIds
      .map((id) => document.getElementById(id))
      .filter((el): el is HTMLElement => el !== null);
    if (elements.length === 0) return;

    function recompute() {
      let current = elements[0]?.id ?? null;
      for (const el of elements) {
        if (el.getBoundingClientRect().top > ACTIVE_HEADING_REFERENCE_LINE_PX) break;
        current = el.id;
      }
      setActiveId(current);
    }

    // threshold:[0,1] + rootMargin:0 lassen den Observer bei jedem Ein-/Austritt eines
    // beliebigen Abschnitts feuern - reicht als Ausloeser, die eigentliche Position wird
    // danach ohnehin komplett neu berechnet (siehe recompute()).
    const observer = new IntersectionObserver(recompute, { threshold: [0, 1] });
    elements.forEach((el) => observer.observe(el));
    recompute();

    // Zusaetzliches Sicherheitsnetz: ein IntersectionObserver-Trigger bleibt theoretisch
    // denkbar knapp aus, wenn ein Scroll-Sprung alle beobachteten Ueberschriften in einem
    // Schritt komplett ueberspringt (nie beobachtet, aber billig genug, um es abzudecken).
    window.addEventListener("scroll", recompute, { passive: true });
    return () => {
      observer.disconnect();
      window.removeEventListener("scroll", recompute);
    };
  }, [headingIds]);

  return activeId;
}

function TocList({ headings, activeId }: { headings: StockAnalysisHeading[]; activeId: string | null }) {
  return (
    <ul className="flex flex-col gap-1 text-sm">
      {headings.map((heading) => (
        <li key={heading.slug} className={heading.depth === 3 ? "ml-3" : undefined}>
          <a
            href={`#${heading.slug}`}
            aria-current={heading.slug === activeId ? "location" : undefined}
            className={cn(
              "block border-l-2 py-1 pl-3 transition-colors",
              heading.slug === activeId
                ? "border-gold font-medium text-ink"
                : "border-transparent text-muted-foreground hover:text-ink",
            )}
          >
            {heading.number}. {heading.text}
          </a>
        </li>
      ))}
    </ul>
  );
}

export function TableOfContents({ headings }: { headings: StockAnalysisHeading[] }) {
  const headerOffset = useHeaderOffset();
  const activeId = useActiveHeadingId(headings.map((h) => h.slug));

  if (headings.length === 0) return null;

  return (
    <>
      <nav
        aria-label="Inhaltsverzeichnis"
        className="hidden w-60 shrink-0 print:hidden lg:block"
        style={{ position: "sticky", top: headerOffset + READING_PROGRESS_BAR_HEIGHT_PX + 16 }}
      >
        <TocList headings={headings} activeId={activeId} />
      </nav>
      {/* Mobil/Tablet-Fallback unterhalb lg (siehe analysis-reader-shell.tsx fuer die
          Breakpoint-Begruendung): aufklappbarer Block statt sticky Sidebar, damit
          "immer wissen, wo man ist" auch unterhalb der Sidebar-Breite gilt. */}
      <details className="group rounded-lg border border-hairline print:hidden lg:hidden">
        <summary className="flex cursor-pointer list-none items-center justify-between gap-1.5 rounded-lg p-3 text-sm font-medium text-ink marker:content-none">
          <span>Inhaltsverzeichnis</span>
          <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-open:rotate-180" />
        </summary>
        <div className="px-3 pb-3">
          <TocList headings={headings} activeId={activeId} />
        </div>
      </details>
    </>
  );
}
